import { IMAGES } from "@/lib/brand";
import { EnquiryForm } from "@/components/EnquiryForm";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const packages = [
  { n: "01", name: "Kedarnath Yatra",         days: "3 · 4 Days",  note: "Kedarnath with helicopter or trek option." },
  { n: "02", name: "Do Dham",                 days: "5 · 6 Days",  note: "Kedarnath and Badrinath, thoughtfully paced." },
  { n: "03", name: "Char Dham",               days: "9 · 11 Days", note: "Yamunotri, Gangotri, Kedarnath and Badrinath." },
  { n: "04", name: "Helicopter Assistance",   days: "1 · 2 Days",  note: "Priority booking assistance and coordination." },
];

const faq = [
  { q: "When is the best time for Kedarnath Yatra?", a: "May to mid-June and mid-September to October are ideal — clearer skies, safer roads." },
  { q: "Do I need to book helicopter separately?",   a: "Our team can guide you through IRCTC bookings and assist with real-time seat availability." },
  { q: "Is Kedarnath Yatra suitable for elderly guests?", a: "Yes — with helicopter and paced itineraries. We plan around each guest's comfort." },
  { q: "What should I pack?",                         a: "Layered warm clothing, sturdy shoes, sunscreen, medication and a light raincoat." },
];

export default function CharDham() {
  return (
    <div data-testid="page-chardham">
      <section className="relative h-[92vh] min-h-[620px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?auto=format&fit=crop&w=2000&q=80"
          alt="Char Dham Yatra - Kedarnath"
          className="absolute inset-0 w-full h-full object-cover kc-hero-image"
        />
        <div className="absolute inset-0 hero-scrim-strong" />
        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 text-[#F8F6F2]">
          <div className="overline text-[#D4AF37] kc-fade-up">The Pilgrimage</div>
          <h1 className="h-display text-[44px] sm:text-[56px] md:text-[72px] lg:text-[88px] font-light leading-[1.02] mt-6 max-w-5xl kc-fade-up kc-delay-1 tracking-[-0.025em]" style={{textShadow:"0 2px 32px rgba(0,0,0,0.35)"}}>
            Your journey <span className="italic text-[#D4AF37]">to the Himalayas</span> begins here.
          </h1>
          <p className="mt-8 max-w-xl text-[#F8F6F2]/80 text-[15px] leading-relaxed kc-fade-up kc-delay-2">Kedarnath. Badrinath. Gangotri. Yamunotri. Curated with reverence.</p>
          <div className="mt-10 flex flex-wrap gap-3 kc-fade-up kc-delay-3">
            <a href="#enquire" className="btn-gold" data-testid="chardham-cta-book">Book Your Yatra Package <ArrowUpRight size={14} /></a>
            <Link to="/contact" className="btn-ghost-light" data-testid="chardham-cta-plan">Plan Your Sacred Journey</Link>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-10 items-end mb-16">
            <div className="col-span-12 md:col-span-7">
              <div className="overline">Yatra Compositions</div>
              <h2 className="font-serif-display text-5xl md:text-6xl mt-6 font-light leading-[1] tracking-[-0.02em]">
                One family. One pace.<br />One blessed <span className="italic text-gold">journey</span>.
              </h2>
            </div>
          </div>

          <div className="border-t border-kc">
            {packages.map((p, i) => (
              <div key={p.name} className="border-b border-kc py-10 md:py-14 grid grid-cols-12 gap-6 md:gap-10 items-center group cursor-pointer" data-testid={`chardham-pkg-${i}`}>
                <div className="col-span-2 md:col-span-1"><span className="index-num">№ {p.n}</span></div>
                <div className="col-span-10 md:col-span-6">
                  <div className="font-serif-display text-4xl md:text-5xl font-light leading-[1.05] tracking-[-0.02em] group-hover:text-gold transition-colors">{p.name}</div>
                </div>
                <div className="col-span-6 md:col-span-3 text-[11px] uppercase tracking-[0.32em] text-ink-mute">{p.days}<div className="mt-2 text-ink-soft normal-case tracking-normal text-[13.5px]">{p.note}</div></div>
                <div className="col-span-6 md:col-span-2 flex justify-end">
                  <ArrowUpRight size={22} className="text-gold transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0F0F0F] text-[#F8F6F2] section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <div className="overline text-[#D4AF37]">Frequently Asked</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-6 font-light leading-[1.05] tracking-[-0.02em]">Answers before the ascent.</h2>
          </div>
          <div className="md:col-span-7 divide-y divide-[#D4AF37]/20">
            {faq.map((f, i) => (
              <details key={i} className="py-6 group" data-testid={`chardham-faq-${i}`}>
                <summary className="cursor-pointer text-lg md:text-xl font-serif-display font-light flex justify-between items-center list-none">
                  {f.q}<span className="text-[#D4AF37] ml-4 text-2xl font-light transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 text-[#F8F6F2]/70 text-[14.5px] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="enquire" className="section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5 img-zoom h-[500px] md:h-full">
            <img src={IMAGES.bell} alt="Temple bell" className="w-full h-full object-cover" />
          </div>
          <div className="md:col-span-7"><EnquiryForm type="chardham" /></div>
        </div>
      </section>
    </div>
  );
}
